import * as React from "react";
import { axiosPatientInstance } from "config";
import { useAxiosMutation } from "hooks";
import { EncounterInsuranceContext } from "modules/reception/contexts";
import {
  InProcessSection,
  InsuranceSection,
  ReceptionSection,
} from "modules/reception/types";
import { useQueryClient } from "react-query";
import axios from "axios";

type UsePatchEncounterFieldProps = {
  reloadInfo?: boolean;
  reloadSections?: (ReceptionSection | InsuranceSection | InProcessSection)[];
  encounterId: string;
};

type UsePatchEncounterFieldPayload = {
  path: string;
  value: unknown;
}[];

function usePatchEncounterField(props: UsePatchEncounterFieldProps) {
  const { encounterId, reloadInfo = true, reloadSections = [] } = props;
  const queryClient = useQueryClient();
  const cancelTokenSourcesRef = React.useRef<
    Set<ReturnType<typeof axios.CancelToken.source>>
  >(new Set());

  const cancel = React.useCallback(() => {
    cancelTokenSourcesRef.current.forEach((source) => {
      source.cancel("Encounter field patch was reset");
    });
    cancelTokenSourcesRef.current.clear();
  }, []);

  const insuranceContext = React.useContext(EncounterInsuranceContext);

  const mutation = useAxiosMutation(
    ["patchEncounterField", props],
    (payload: UsePatchEncounterFieldPayload) => {
      const source = axios.CancelToken.source();
      cancelTokenSourcesRef.current.add(source);

      return axiosPatientInstance
        .patch(`v1/encounter/${encounterId}/form/save`, payload, {
          cancelToken: source.token,
        })
        .finally(() => {
          cancelTokenSourcesRef.current.delete(source);
        });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["encounterFormStatus", { encounterId }]);
        if (reloadInfo) {
          if (reloadSections.length === 0 || reloadSections.includes("info")) {
            queryClient.invalidateQueries(["encounterInfo", { encounterId }]);
            queryClient.invalidateQueries([
              "chartPatientCard",
              { encounterId },
            ]);
          }

          if (
            reloadSections.includes("sideInfo") ||
            reloadSections.length === 0
          )
            queryClient.invalidateQueries([
              "encounterSideInfo",
              { encounterId },
            ]);
          if (
            reloadSections.length === 0 ||
            reloadSections.includes("overview")
          )
            queryClient.invalidateQueries([
              "encounterOverview",
              { encounterId },
            ]);
          if (
            insuranceContext &&
            (reloadSections.length === 0 ||
              reloadSections.some((sect) => sect.startsWith("insurance")))
          )
            queryClient.invalidateQueries([
              "encounterInsurance",
              { encounterId },
            ]);
          // Get only the insurance relevant sections
          insuranceContext?.setReloadSections([
            ...(reloadSections.filter((sect) =>
              sect.startsWith("insurance")
            ) as InsuranceSection[]),
          ]);
          if (
            reloadSections.length === 0 ||
            reloadSections.includes("inProcess")
          ) {
            queryClient.invalidateQueries(["patientDetails", { encounterId }]);
            queryClient.invalidateQueries(["inProcessEncounterList"]);
            queryClient.invalidateQueries(["patientInfo", { encounterId }]);
          }
        }
      },
    }
  );

  React.useEffect(() => cancel, [cancel]);

  return { ...mutation, cancel };
}

export type { UsePatchEncounterFieldProps };
export { usePatchEncounterField };
